<!-- @format -->

<template>
	<el-container
		class="pc-layout-main-container"
		:class="[ltrGap ? 'pc-layout-main-container-ltr' : '', ttbGap ? 'pc-layout-main-container-ttb' : '']"
	>
		<!-- left -->
		<el-aside
			v-if="$slots['aside']"
			ref="resizableBox"
			class="pc-layout-main-container-left"
			:style="isDragAside ? { width: `${currentWidth}px` } : {}"
		>
			<slot name="aside" />
			<div v-if="isDragAside" ref="resizeHandle" class="resize-handle"></div>
		</el-aside>
		<!-- divider -->
		<div
			v-if="$slots['aside'] && ($slots['content-header'] || $slots['content-main'])"
			class="el-divider el-divider--vertical"
		/>
		<!-- content -->
		<el-container
			v-if="$slots['content-header'] || $slots['content-main']"
			class="pc-layout-main-container-content"
			:class="[$slots['content-header'] ? 'is-vertical' : '']"
		>
			<!-- header -->
			<el-header v-if="$slots['content-header']" class="pc-layout-main-container-content-header">
				<slot name="content-header" />
			</el-header>
			<!-- divider -->
			<div v-if="$slots['content-header'] && $slots['content-main']" class="el-divider el-divider--horizontal" />
			<!-- main -->
			<el-main v-if="$slots['content-main']" class="pc-layout-main-container-content-main">
				<slot name="content-main" />
			</el-main>
		</el-container>
	</el-container>
</template>

<script setup lang="ts">
	import { ref } from 'vue';
	type GapPlacement = 'ltr' | 'ttb';

	interface Props {
		gapPlacement?: GapPlacement[];
		isDragAside?: boolean;
		dragMinWidht?: number;
		dragMaxWidht?: number;
		asideWidth?: number;
	}

	const props = withDefaults(defineProps<Props>(), {
		gapPlacement: () => [],
		isDragAside: false,
	});

	const ltrGap = computed(() => props.gapPlacement.includes('ltr'));
	const ttbGap = computed(() => props.gapPlacement.includes('ttb'));

	// 组件状态
	const resizableBox = ref();
	const resizeHandle = ref();
	const currentWidth = ref(props.asideWidth || 200); // 初始宽度
	const isDragging = ref(false);
	const startX = ref(0);
	const startWidth = ref(0);

	// 宽度限制配置
	const minWidth = props.dragMinWidht || 100;
	const maxWidth = props.dragMaxWidht || 500;

	// 处理鼠标按下事件 - 开始拖动
	function handleMouseDown(e) {
		isDragging.value = true;
		startX.value = e.clientX;
		startWidth.value = currentWidth.value;
		console.log(resizableBox.value, 'value');
		// 添加拖动中的样式类
		resizableBox.value.$el.classList.add('dragging');
	}

	// 处理鼠标移动事件 - 调整宽度
	function handleMouseMove(e) {
		if (!isDragging.value) return;

		// 计算新宽度
		const deltaX = e.clientX - startX.value;
		let newWidth = startWidth.value + deltaX;

		// 限制在最小和最大宽度之间
		newWidth = Math.max(minWidth, Math.min(newWidth, maxWidth));

		// 更新宽度
		currentWidth.value = newWidth;
	}

	// 处理鼠标释放事件 - 结束拖动
	function handleMouseUp() {
		if (!isDragging.value) return;

		isDragging.value = false;

		// 移除拖动中的样式类
		resizableBox.value.$el.classList.remove('dragging');
	}
	const initMouseEvent = () => {
		if (!props.isDragAside) return;
		// 为拖动手柄绑定鼠标按下事件
		resizeHandle.value.addEventListener('mousedown', handleMouseDown);
		// 为document绑定鼠标移动和释放事件（全局监听，确保拖动流畅）
		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);

		// 组件卸载时清理事件监听
		return () => {
			resizeHandle.value.removeEventListener('mousedown', handleMouseDown);
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};
	};
	// 组件挂载时绑定事件监听
	onMounted(() => {
		initMouseEvent();
	});
</script>

<style scoped lang="scss">
	/* 拖动手柄样式 */
	.resize-handle {
		position: absolute;
		top: 0;
		right: 0;
		width: 3px;
		height: 100%;
		cursor: col-resize;
		background-color: transparent;
		transition: background-color 0.2s ease;
	}

	.resize-handle:hover {
		background-color: #dcdfe6;
	}

	.pc-layout-main-container-left.dragging {
		box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
		user-select: none; /* 防止拖动时选中文本 */
	}

	.pc-layout-main-container {
		height: 100%;

		.pc-layout-main-container-left {
			width: auto;
			position: relative;
		}

		.pc-layout-main-container-content > .pc-layout-main-container-content-header {
			padding: 0px;
			height: auto;
		}

		.pc-layout-main-container-content > .pc-layout-main-container-content-main {
			padding: 0px;
			overflow: hidden;
			flex-basis: 0%;
		}

		.el-divider {
			flex-shrink: 0;
		}

		.el-divider--vertical {
			height: 100%;
		}

		.el-divider--horizontal {
			margin: 8px 0;
		}
	}

	.pc-layout-main-container-ltr > .el-divider--vertical {
		width: 16px;
		border: none;
		margin: 0;
		background-color: var(--theme-bg-color);
	}

	.pc-layout-main-container-ltr > .pc-layout-main-container-left {
		padding: 16px;
	}

	.pc-layout-main-container-ltr > .pc-layout-main-container-content {
		padding: 16px 32px;
	}

	.pc-layout-main-container-ttb > .pc-layout-main-container-content > .el-divider--horizontal {
		height: 10px;
		border: none;
		margin: 0;
		background-color: var(--theme-bg-color);
	}

	.pc-layout-main-container-ttb > .pc-layout-main-container-left {
		padding: 16px;
	}

	.pc-layout-main-container-ttb > .pc-layout-main-container-content {
		padding: 0px;
	}

	.pc-layout-main-container-ttb > .pc-layout-main-container-content > .pc-layout-main-container-content-header {
		padding: 16px 32px;
	}

	.pc-layout-main-container-ttb > .pc-layout-main-container-content > .pc-layout-main-container-content-main {
		padding: 16px 32px;
	}
</style>
